import { AssetType } from './../../../asset-details/types';
import type { PrecisionFloat } from '@bts-web/utils-formatting';
import { Locale } from '@bts-web/utils-lokalise';
import { AmountFor } from '@bts-web/data-layer/server';
import {
  valueFromAssetToFiat,
  valueFromFiatToAsset,
  getAssetDisplayValueFromSource,
} from '../../../common';
import type { MaximumTradeAmountType, MutableNonNullableAsset } from '../types';
import { getConvertedValueExplanation } from './getConvertedValueExplanation';
import { TradeControllerVariants } from '../../utils';
import { multiplyTradeAmountByExchangeRate } from './multiplyTradeAmountByExchangeRate';

const getOppositeDisplayType = (displayType: AmountFor) =>
  displayType === 'FIAT' ? 'ASSET' : 'FIAT';

export type ConversionSettings = {
  value: string;
  displayType: AmountFor;
  tradeType: TradeControllerVariants;
  assetType: AssetType;
  locale: Locale;
  currency: string;
  symbol: MutableNonNullableAsset['symbol'];
  translations: {
    inclSpread: string;
    inclFees: string;
    afterSpread: string;
    afterFees: string;
    afterFeesAtCurrentPrice?: string;
    afterSpreadAtCurrentPrice?: string;
    shares?: string;
  };
  swap?: {
    targetSymbol: string;
    swapExchangeRate: PrecisionFloat; // Direct exchange rate from source to target
  };
  assetPrice: PrecisionFloat;
  useRawSource?: {
    rawSourceValue: PrecisionFloat;
    rawSourceDisplayType: AmountFor;
  };
  maximumTradeAmount?: MaximumTradeAmountType;
  isRealStockAsset?: boolean;
  // Confirm screen locks the real-stock price, so the quantity is exact ("=" +
  // "incl. fees"). Left undefined elsewhere (e.g. Create), where it stays "≈".
  isEstimation?: boolean;
};

export const getConvertedSummary = ({
  assetPrice,
  assetType,
  tradeType,
  currency,
  displayType,
  locale,
  symbol,
  translations,
  value,
  swap,
  useRawSource,
  maximumTradeAmount,
  isRealStockAsset,
  isEstimation,
}: ConversionSettings) => {
  // For swap transactions, we always work with assets, not FIAT
  const isFiat = displayType === 'FIAT' && !swap;

  // Clean the input value
  const workingValue = (() => {
    if (useRawSource?.rawSourceValue?.value) {
      return useRawSource.rawSourceValue.value;
    }

    const cleaned = value.replace(/,/g, '.');

    // Return '0' for empty or invalid values to prevent NaN
    return cleaned === '' || cleaned === '.' ? '0' : cleaned;
  })();

  // Determine target display type
  const targetDisplayType = (() => {
    if (useRawSource) {
      return useRawSource.rawSourceDisplayType;
    }

    if (swap) {
      // Swaps always show asset amounts
      return 'ASSET';
    }

    return getOppositeDisplayType(displayType);
  })();

  // Calculate the converted amount
  let conversionAmount: PrecisionFloat | undefined;

  if (useRawSource) {
    // Case 1: Accept Offer context
    conversionAmount = useRawSource.rawSourceValue;
  } else if (swap && swap.swapExchangeRate) {
    // Case 2: Swap transactions - use direct exchange rate
    // User enters source asset amount, we show target asset amount
    conversionAmount = multiplyTradeAmountByExchangeRate(
      workingValue,
      swap.swapExchangeRate,
    );
  } else if (!swap) {
    // Case 3: Regular buy/sell transactions
    if (isFiat) {
      // FIAT to ASSET conversion
      conversionAmount = valueFromFiatToAsset(workingValue, assetPrice);
    } else {
      // ASSET to FIAT conversion
      conversionAmount = valueFromAssetToFiat(workingValue, assetPrice);
    }
  }

  // Handle maximum trade amount edge cases
  if (maximumTradeAmount && conversionAmount && !swap) {
    // For UNITS is selected (asset) and the max value was input,
    // we want to convert the value to fiat using sell price (asset price) instead of balance amount
    if (!isFiat && workingValue === maximumTradeAmount?.ASSET?.value) {
      conversionAmount = valueFromAssetToFiat(
        maximumTradeAmount.ASSET.value,
        assetPrice,
      );
    } else if (
      isFiat &&
      tradeType !== 'buy' &&
      workingValue === maximumTradeAmount?.FIAT?.value
    ) {
      conversionAmount = maximumTradeAmount.ASSET;
    }
  }

  let customSymbol = swap ? swap.targetSymbol : symbol;

  if (isRealStockAsset) {
    customSymbol = undefined;
  }

  // Format the final display value
  const displayValue = getAssetDisplayValueFromSource({
    sourceAmount: conversionAmount?.value ?? '0',
    sourceType: targetDisplayType,
    currency: isRealStockAsset || swap ? undefined : currency, // No currency for swap displays
    locale,
    customSymbol,
  });

  // For RTL languages, the approximation symbol should come after the value
  const convertedValue = `≈ ${displayValue}`;

  // Get explanation text
  const explanation = getConvertedValueExplanation(
    assetType,
    tradeType,
    translations,
  );

  if (isRealStockAsset) {
    // Confirm screen (isEstimation === false) locks the price, so the quantity is
    // exact: "= {qty} shares incl. fees" (qty already reflects (amount − fee) ÷
    // offer price). Elsewhere the price is an estimation and stays "≈ {qty} shares".
    return isEstimation === false
      ? `= ${displayValue} ${translations.shares} ${translations.inclFees}`
      : `${convertedValue} ${translations.shares}`;
  }

  return `${convertedValue} ${explanation}`;
};
